import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { Rfi, RFI_STATUSES, RfiStatus } from '../../objects/rfi';
import { RfiService } from '../../Services/rfi.service';
import { Project } from '../../objects/Project';

@Component({
  selector: 'app-project-rfi',
  templateUrl: './project-rfi.component.html',
  styleUrls: ['./project-rfi.component.css']
})
export class ProjectRfiComponent implements OnInit, OnDestroy {
  @Input() project?: Project;

  rfis: Rfi[] = [];
  editingRfi: Rfi | null = null;
  showForm = false;
  isLoading = false;
  statusOptions = RFI_STATUSES;

  form: FormGroup;
  private subs: Subscription[] = [];

  displayedColumns = ['rfiNumber', 'subject', 'submittedTo', 'dateRequired', 'status', 'ballInCourt', 'actions'];

  constructor(
    private rfiService: RfiService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.load();
    this.subs.push(
      this.rfiService.rfis$.subscribe(r => { this.rfis = r; })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  load(): void {
    if (!this.project?.id) { return; }
    this.isLoading = true;
    const sub = this.rfiService.getByProject(this.project.id).subscribe({
      next: () => (this.isLoading = false),
      error: () => (this.isLoading = false)
    });
    this.subs.push(sub);
  }

  openAdd(): void {
    this.editingRfi = null;
    this.form.reset({
      rfiNumber: this.rfiService.nextRfiNumber(this.rfis),
      status: 'Open',
      costImpact: 0,
      scheduleImpactDays: 0,
      dateSubmitted: new Date().toISOString().split('T')[0]
    });
    this.showForm = true;
  }

  openEdit(rfi: Rfi): void {
    this.editingRfi = rfi;
    this.form.patchValue(rfi);
    this.showForm = true;
  }

  cancel(): void {
    this.showForm = false;
    this.editingRfi = null;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value: Partial<Rfi> = {
      ...this.form.value,
      projectId: this.project?.id ?? 0
    };

    if (this.editingRfi?.id) {
      const sub = this.rfiService.update(this.editingRfi.id, value as Rfi).subscribe({
        next: () => {
          this.snackBar.open('RFI updated', '', { duration: 2500 });
          this.cancel();
        }
      });
      this.subs.push(sub);
    } else {
      const sub = this.rfiService.create(value).subscribe({
        next: () => {
          this.snackBar.open('RFI created', '', { duration: 2500 });
          this.cancel();
        }
      });
      this.subs.push(sub);
    }
  }

  remove(rfi: Rfi): void {
    if (!confirm(`Delete ${rfi.rfiNumber}?`)) { return; }
    if (!rfi.id) { return; }
    const sub = this.rfiService.delete(rfi.id).subscribe({
      next: () => this.snackBar.open('RFI deleted', '', { duration: 2500 })
    });
    this.subs.push(sub);
  }

  get openCount(): number { return this.rfis.filter(r => r.status === 'Open').length; }
  get answeredCount(): number { return this.rfis.filter(r => r.status === 'Answered').length; }
  get totalCostImpact(): number { return this.rfis.reduce((s, r) => s + (r.costImpact || 0), 0); }
  get totalScheduleImpact(): number { return this.rfis.reduce((s, r) => s + (r.scheduleImpactDays || 0), 0); }

  statusClass(status: RfiStatus): string {
    const map: Record<RfiStatus, string> = {
      Open: 'status-open',
      Answered: 'status-answered',
      Closed: 'status-closed',
      Void: 'status-void'
    };
    return map[status] || '';
  }

  isOverdue(rfi: Rfi): boolean {
    if (rfi.status === 'Answered' || rfi.status === 'Closed') { return false; }
    return new Date(rfi.dateRequired) < new Date();
  }

  private buildForm(): FormGroup {
    return new FormGroup({
      rfiNumber: new FormControl('', Validators.required),
      subject: new FormControl('', Validators.required),
      description: new FormControl('', Validators.required),
      submittedBy: new FormControl('', Validators.required),
      submittedTo: new FormControl('', Validators.required),
      dateSubmitted: new FormControl('', Validators.required),
      dateRequired: new FormControl('', Validators.required),
      dateAnswered: new FormControl(''),
      answer: new FormControl(''),
      status: new FormControl('Open', Validators.required),
      ballInCourt: new FormControl(''),
      costImpact: new FormControl(0, Validators.min(0)),
      scheduleImpactDays: new FormControl(0, Validators.min(0))
    });
  }
}
